import { tenantApi } from '@shared/api/tenantApi';
import { unwrapList } from '@shared/api/unwrap';

/**
 * Suppliers directory — built from two member endpoints:
 *   - `GET /supplier-categories/member` → the supplier category list
 *   - `GET /suppliers/member`           → active suppliers (minimal safe fields)
 *
 * Both are `{ success, data }` enveloped. Suppliers are grouped by their
 * `supplier_category_id` client-side; uncategorised ones fall into "Other".
 * Mirrors features/directory/api.ts.
 */

export interface SupplierItem {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface SupplierGroup {
  id: string;
  name: string;
  suppliers: SupplierItem[];
}

interface RawCategory {
  supplier_category_id?: string;
  name?: string;
  display_order?: number | null;
}

interface RawSupplier {
  supplier_id?: string;
  name?: string;
  supplier_category_id?: string | null;
  phone?: string | null;
  email?: string | null;
}

function toItem(s: RawSupplier, key: string): SupplierItem {
  return {
    id: s.supplier_id ?? key,
    name: s.name ?? 'Supplier',
    phone: s.phone ?? undefined,
    email: s.email ?? undefined,
  };
}

export async function fetchSupplierGroups(): Promise<SupplierGroup[]> {
  const [catRes, itemRes] = await Promise.all([
    tenantApi.get('/supplier-categories/member'),
    tenantApi.get('/suppliers/member', { params: { limit: 200 } }),
  ]);

  const categories = unwrapList<RawCategory>(catRes.data);
  const items = unwrapList<RawSupplier>(itemRes.data);

  const groups: SupplierGroup[] = categories
    .map((cat, gi) => ({
      id: cat.supplier_category_id ?? `group-${gi}`,
      name: cat.name ?? 'Category',
      suppliers: items
        .filter((s) => s.supplier_category_id === cat.supplier_category_id)
        .map((s, ci) => toItem(s, `s-${gi}-${ci}`)),
    }))
    .filter((g) => g.suppliers.length > 0);

  const uncategorised = items.filter((s) => !s.supplier_category_id);
  if (uncategorised.length > 0) {
    groups.push({
      id: 'other',
      name: 'Other',
      suppliers: uncategorised.map((s, ci) => toItem(s, `o-${ci}`)),
    });
  }

  return groups;
}
